import useModal from '@area2k/use-modal';
import { useApolloClient } from '@apollo/client';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { Navigate, useSearchParams } from 'react-router-dom';

import useAuth from '@/hooks/useAuth';
import useOrderFlowV2Enabled from '@/hooks/useOrderFlowV2Enabled';
import { paySurgeClient } from '@/routes/Agency/PaySurge/api';
import PostingSurgeDialog, {
  PostingSurgeItem,
} from '@/routes/Agency/PaySurge/components/PostingSurgeDialog';

import { GAEvent } from '@/constants/gaevents';
import useAnalytics from '@/util/analytics';
import { evictDashboardShiftWeeks } from '@/util/apollo/dashboardCache';
import { isIOS, isWeb } from '@/util/platform';
import {
  OrderCreationStepKey,
  computeOrderTotalValue,
  getOrderFlowStartMs,
  setOrderFlowClientId,
  trackOrderCreationStarted,
  trackOrderCreationStepCompleted,
  trackOrderCreationStepViewed,
  trackOrderReposted,
  trackOrderSubmitted,
  trackTemplateSaved,
} from '@/util/vexo';

import EntryScreen from './EntryScreen';
import OrderV2EntryOnboarding from './onboarding/OrderV2EntryOnboarding';
import { buildStaffingAggregates } from './utils/buildStaffingAggregates';
import {
  buildCreatePayload,
  buildOrderValueJobsFromState,
} from './api/buildCreatePayload';
import { createOrderV2Client } from './api/client';
import { orderV2QueryKeys } from './api/queryKeys';
import {
  BillingAccountV2,
  OrderTemplateDetail,
  SavedSnippet,
  SavedSnippetFieldKind,
} from './api/types';
import useCustomersDirectory from './hooks/useCustomersDirectory';
import useCertificates from './hooks/useCertificates';
import useLocations from './hooks/useLocations';
import useOrderFlowTour from './hooks/useOrderFlowTour';
import useOrderTemplate from './hooks/useOrderTemplate';
import useOrderTemplatesList from './hooks/useOrderTemplatesList';
import usePaymentMethods from './hooks/usePaymentMethods';
import useRateQuote from './hooks/useRateQuote';
import useSavedSnippets from './hooks/useSavedSnippets';
import useShiftSupervisors from './hooks/useShiftSupervisors';
import useSkillCategories from './hooks/useSkillCategories';
import useSkills from './hooks/useSkills';
import useSurgeRates from './hooks/useSurgeRates';
import useUniforms from './hooks/useUniforms';
import useVerificationFlag from './hooks/useVerificationFlag';
import { postingPreviewInputs, postingSurgeItems } from './utils/postingSurge';
import {
  initialOrderStateV2,
  defaultJobDraftV2,
  JobDraftV2,
  OrderActionsContext,
  OrderStateContext,
  OrderStateV2,
} from './context';
import WizardHeader from './components/WizardHeader';
import BrowseSavedModal from './components/modals/BrowseSavedModal';
import SaveSnippetModal from './components/modals/SaveSnippetModal';
import PostSubmitModal from './components/modals/PostSubmitModal';
import ExitWizardModal from './components/modals/ExitWizardModal';
import {
  ErrorBlock,
  FooterCancel,
  FooterGhost,
  FooterPrimary,
  StickyFooter,
  WizardBody,
  WizardShell,
} from './styles';
import InstructionsStep from './Steps/InstructionsStep';
import JobTypeStep from './Steps/JobTypeStep';
import PayRateStep from './Steps/PayRateStep';
import ReviewStep from './Steps/ReviewStep';
import ScheduleStep from './Steps/ScheduleStep';
import StaffingStep from './Steps/StaffingStep';
import {
  addNewJobDraftReducer,
  applyRateQuoteReducer,
  autoSeedCertificateDefaultsReducer,
  withCertificateDefaults,
  unseededDraftsSignature,
  activeCertificateDefaults,
  cancelEditJobDraftReducer,
  cancelNewJobDraftReducer,
  dedupeTemplateJobs,
  editJobDraftReducer,
  finishEditJobDraftReducer,
  finishNewJobDraftReducer,
} from './stateReducers';

const BASE_STEP_LABELS = [
  'Shift type',
  'Schedule',
  'Staffing',
  'Instructions',
  'Submission',
];
// Markup billing accounts add a "Pay Rate" step right before Submission,
// mirroring the legacy CreateGigOrder flow.
const MARKUP_STEP_LABELS = [
  'Shift type',
  'Schedule',
  'Staffing',
  'Instructions',
  'Pay Rate',
  'Submission',
];

// Map the wizard's local 0-indexed step (+ markup flag + invites sub-step)
// to the spec's `OrderCreationStepKey`. `staffingSubstep === 'invite'` while
// on the staffing step (index 2) flips the key to `'invites'`, which makes
// `trackOrderCreationStepViewed` fire `order_creation_step_viewed_3b`.
const stepKeyFor = (
  step: number,
  isMarkup: boolean,
  staffingSubstep: 'choice' | 'invite',
): OrderCreationStepKey | null => {
  // Job type / Schedule are the same across markup and base.
  if (step === 0) return 'job_type';
  if (step === 1) return 'schedule';
  if (step === 2) return staffingSubstep === 'invite' ? 'invites' : 'staffing';
  if (step === 3) return 'instructions';
  if (isMarkup) {
    if (step === 4) return 'pay_rate';
    if (step === 5) return 'summary';
  } else if (step === 4) {
    return 'summary';
  }
  return null;
};
const VIEW_ENTRY = 'entry';
const VIEW_WIZARD = 'wizard';
type ViewMode = typeof VIEW_ENTRY | typeof VIEW_WIZARD;

type SnippetApplyHandler = (body: string) => void;

type SnippetPanel =
  | {
      mode: 'browse';
      fieldKind: SavedSnippetFieldKind;
      title: string;
      onApply: SnippetApplyHandler;
    }
  | {
      mode: 'save';
      fieldKind: SavedSnippetFieldKind;
      title: string;
      currentBody: string;
    }
  | { mode: 'closed' };

const closedPanel: SnippetPanel = { mode: 'closed' };

const AgencyCreateOrderV2 = () => {
  const [state, setState] = useState<OrderStateV2>(initialOrderStateV2);
  const {
    billingAccounts,
    customers: directoryCustomers,
    refresh: refreshDirectory,
  } = useCustomersDirectory();
  const [viewMode, setViewMode] = useState<ViewMode>(VIEW_ENTRY);
  // Legacy v1 `ExitJobEditorModal` only pops once the user has progressed
  // past the first step (its `state.completedStep` truthiness gate). v2
  // mirrors that with `step > 0 || isPrefilled` — if the user clicked X
  // before doing anything substantive, we close silently like before.
  const [exitConfirmOpen, setExitConfirmOpen] = useState(false);
  const [snippetPanel, setSnippetPanel] = useState<SnippetPanel>(closedPanel);
  const [browseSnippets, setBrowseSnippets] = useState<SavedSnippet[]>([]);
  const [browseLoading, setBrowseLoading] = useState(false);
  const [applyingSnippetId, setApplyingSnippetId] = useState<number | null>(
    null,
  );
  const [savingSnippet, setSavingSnippet] = useState(false);
  const [snippetError, setSnippetError] = useState<string | null>(null);
  // Captured when the user enters a step so we can report `time_on_step_sec`
  // on `order_creation_step_completed`. Reset on every step transition.
  const [stepStartMs, setStepStartMs] = useState<number>(() => Date.now());
  // The flow clock is (re)stamped by trackOrderCreationStarted at each wizard
  // entry — see onStartOrder/onSelectTemplate, which refresh this via
  // setFlowStartMs so a second in-route attempt times from its own start, not
  // the first. Fallback covers deep-links that skip the entry surface.
  const [flowStartMs, setFlowStartMs] = useState<number>(
    () => getOrderFlowStartMs() ?? Date.now(),
  );
  // Template id when the wizard was launched from a template. Used to
  // emit `order_reposted` at submit time once the new order_id is known.
  const [repostFromTemplateId, setRepostFromTemplateId] = useState<
    number | null
  >(null);

  const [skillSearch, setSkillSearch] = useState('');
  const billingAccountId = state.billingAccount?.id ?? null;
  const { templates } = useOrderTemplatesList(
    billingAccountId,
    state.orderType,
  );
  const { skills, skillsLoading, skillsSettled, skillsError } = useSkills(
    billingAccountId,
    skillSearch,
  );
  const { skillCategories } = useSkillCategories(billingAccountId);
  const { paymentMethods, paymentMethodsLoading, paymentMethodsError } =
    usePaymentMethods(billingAccountId);
  const { uniforms, uniformsLoading, uniformsError } =
    useUniforms(billingAccountId);
  const { addressSnippets, checkInSnippets, refreshSnippetsForKind } =
    useSavedSnippets(billingAccountId);
  const { locations, refreshLocations } = useLocations(billingAccountId);
  const { shiftSupervisors, refreshShiftSupervisors } =
    useShiftSupervisors(billingAccountId);
  const { surgeRates } = useSurgeRates(
    billingAccountId,
    !!state.billingAccount?.enable_surge_rate,
  );
  const [submitLoading, setSubmitLoading] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);
  // GRAVY-10881 — shifts that surge the moment this order posts them, held
  // with the payload while the admin reads the warning.
  const [postingSurge, setPostingSurge] = useState<{
    payload: ReturnType<typeof buildCreatePayload>;
    items: PostingSurgeItem[];
  } | null>(null);
  const [orderData, setOrderData] = useState<{
    orderId: number | string;
    hireMessage: string | null;
    // GRAVY-10803: per-worker refusals from a partially-staffed order.
    staffingErrors: string[];
  } | null>(null);

  const { logEvent } = useAnalytics();
  const { startTour } = useOrderFlowTour();
  const queryClient = useQueryClient();
  const apolloClient = useApolloClient();
  const { currentAgency, currentAdminIsTenantAdminRole } = useAuth();

  // Staffing step has an internal sub-step: when "Send Invitations" is the
  // strategy, the worker list is hidden behind a "Continue to Invite"
  // transition. LTA always starts on 'invite' since it has no choice card.
  const [staffingSubstep, setStaffingSubstep] = useState<'choice' | 'invite'>(
    'choice',
  );

  // Reset the staffing sub-step whenever the wizard navigates away from the
  // staffing step, so re-entering the step always starts at the choice cards.
  // (No LTA→'invite' flip here — that post-render flip fired a spurious _3;
  // resolvedStaffingSubstep below resolves it synchronously instead.)
  useEffect(() => {
    if (state.step !== 2) setStaffingSubstep('choice');
  }, [state.step]);

  // LTA has no choice screen, so it is always on the invite sub-step while on
  // staffing. Resolve this synchronously (not via a post-render effect) so the
  // view effect never observes a transient 'choice' for LTA. Read this value
  // everywhere the *displayed* sub-step matters; keep writing the underlying
  // `staffingSubstep` for the GIG choice→invite transition.
  const resolvedStaffingSubstep =
    state.step === 2 && state.orderType === 'LTA' ? 'invite' : staffingSubstep;

  // Vexo Staffing `step_completed` aggregates. invited/hired counts come
  // from `state.jobs[].invite_workers/hire_workers` at handoff time. The
  // OT counters are populated by the StaffingStep's invite/hire toggles
  // (GRAVY-10099 — V2 wizard now invokes `useOvertimeGate`).
  const staffingOTCounters = useRef({
    shown: 0,
    accepted: 0,
    dismissed: 0,
  });
  useEffect(() => {
    if (state.step === 2) {
      staffingOTCounters.current = { shown: 0, accepted: 0, dismissed: 0 };
    }
  }, [state.step]);
  const handleStaffingOvertimeOutcome = useCallback(
    (kind: 'shown' | 'accepted' | 'dismissed') => {
      staffingOTCounters.current[kind] += 1;
    },
    [],
  );

  // Tracks whether every job's pay rate meets its market minimum on the
  // PayRateStep. Only consulted when the markup-only Pay Rate step is shown.
  const [payRateValid, setPayRateValid] = useState(false);
  // Mirrors v1's CustomErrorsForm gate on the editable rate cards: the
  // ScheduleStep (STATIC tenant-admin pay/bill inputs) and ReviewStep
  // (tenant-admin pay rate input on markup accounts) report below-minimum
  // edits up here so Continue and Submit can be disabled. Defaults to true
  // so an unmounted step never blocks navigation — each step flips this
  // to false only when an actual below-min edit is detected.
  const [scheduleRatesValid, setScheduleRatesValid] = useState(true);
  const [reviewRatesValid, setReviewRatesValid] = useState(true);
  // V1 parity: the legacy Billing form refused to start the order when
  // the billing account lacked a `currentPaymentMethod`. V2 mirrors that
  // gate on the Place new order button — ReviewStep reports validity
  // once the payment-methods fetch lands and confirms a current method.
  // Defaults to false so the in-flight window between Review mounting
  // and its first validity report can't briefly render Submit as
  // enabled: per the QA contract, a loading state must never behave
  // like a successful verification state. The submit footer is only
  // rendered on the Review step, so a false default before Review is
  // reached has no effect on earlier steps.
  const [paymentMethodValid, setPaymentMethodValid] = useState(false);
  // Submit gate for multi-skill repeat orders: ReviewStep flips this to
  // false while any job's saved `skill_id` does not resolve against the
  // loaded skills list (template skill since deactivated / un-requestable
  // / agency moved). Defaults to true so the gate never trips while the
  // `/skills` fan-out is still in-flight.
  const [reviewSkillsValid, setReviewSkillsValid] = useState(true);
  // Submit gate for jobs with no schedule yet — ReviewStep flips this to
  // false while any job in the order carries an empty `shifts: []`
  // (typical of a multi-skill template's non-first skill that was never
  // taken through the schedule step). Defaults to true so an empty
  // order or in-flight mount never trips the gate.
  const [reviewShiftsValid, setReviewShiftsValid] = useState(true);

  const [searchParams] = useSearchParams();

  const lastViewedKeyRef = useRef<string | null>(null);

  // Vexo: fire the matching `order_creation_step_viewed_N` event every
  // time the active step (or the staffing sub-step) changes while the
  // user is inside the wizard. Each step has its own event name per the
  // spec; `_3b` fires when the staffing sub-step switches to 'invite'.
  // Skips fires when the wizard isn't visible so back-to-entry returns
  // don't generate noise.
  useEffect(() => {
    if (viewMode !== VIEW_WIZARD) return;
    const stepKey = stepKeyFor(state.step, isMarkup, resolvedStaffingSubstep);
    if (!stepKey) return;
    // Dedup consecutive identical keys (render churn / StrictMode double-invoke).
    // A fresh wizard session resets this ref (see onStartOrder/onSelectTemplate),
    // so the first step view of every new attempt still fires.
    if (lastViewedKeyRef.current === stepKey) return;
    lastViewedKeyRef.current = stepKey;
    trackOrderCreationStepViewed({
      step: stepKey,
      time_since_flow_start_sec: Math.max(
        0,
        Math.round((Date.now() - flowStartMs) / 1000),
      ),
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [state.step, resolvedStaffingSubstep, viewMode]);

  useEffect(() => {
    const typeParam = searchParams.get('type');
    if (typeParam === 'GIG' || typeParam === 'LTA') {
      setState((prev) =>
        prev.orderType === typeParam
          ? prev
          : {
              ...prev,
              orderType: typeParam,
              jobs:
                prev.jobs.length > 0
                  ? prev.jobs.map((job) => ({ ...job, job_type: typeParam }))
                  : [defaultJobDraftV2(typeParam)],
            },
      );
    }
  }, [searchParams]);

  const customerId = state.billingAccount?.customer_id ?? null;

  const job0 = state.jobs[0];
  const job0SkillId = job0?.skill_id ?? null;
  const job0AddressId = job0?.address_id ?? null;
  // `pay_rate_touched` counts as "has rates" so that clearing the field to
  // retype it does not read as "never quoted" and pull the cached quote back
  // in on top of the user (GRAVY-10445).
  const job0HasRates = !!(
    job0 &&
    (job0.pay_rate || job0.cost_rate || job0.pay_rate_touched)
  );

  const actions = useMemo(
    () => ({
      setBillingAccount: (billingAccount: BillingAccountV2 | null) =>
        setState((prev) => ({
          ...prev,
          billingAccount,
          // Switching billing accounts invalidates any prior template prefill
          // since templates are billing-account scoped.
          sourceTemplateId: null,
          isPrefilled: false,
          jobs: prev.orderType
            ? [
                withCertificateDefaults(
                  defaultJobDraftV2(prev.orderType),
                  // Only if the incoming account is the one the stored
                  // defaults came from (e.g. re-picking the same account).
                  billingAccount?.id ===
                    prev.customerDefaultCertificates.billingAccountId
                    ? prev.customerDefaultCertificates.ids
                    : [],
                ),
              ]
            : [],
          // NOT cleared here on purpose: the stored defaults carry the account
          // they belong to, so a previous client's are already inert. Clearing
          // raced the certificates response and wiped a seed that had just
          // landed for the account being picked.
          pendingNewJob: false,
          editSnapshot: null,
          editIndex: null,
        })),
      setOrderType: (orderType: 'GIG' | 'LTA' | null) =>
        setState((prev) => ({
          ...prev,
          orderType,
          jobs:
            prev.jobs.length > 0
              ? prev.jobs.map((job) => ({
                  ...job,
                  job_type: orderType || 'GIG',
                }))
              : orderType
                ? [
                    // The client's certificates may already have resolved
                    // while `jobs` was still empty, in which case the seeding
                    // effect has fired and will not fire again. Seed from the
                    // stored ids so this draft is not left blank.
                    withCertificateDefaults(
                      defaultJobDraftV2(orderType),
                      activeCertificateDefaults(prev),
                    ),
                  ]
                : [],
        })),
      applyTemplate: (template: OrderTemplateDetail) => {
        // OpenAPI types `order_type` as a plain string; the live builder
        // only emits 'GIG' or 'LTA'. Narrow at the boundary.
        const orderType: 'GIG' | 'LTA' =
          template.order_type === 'LTA' ? 'LTA' : 'GIG';
        setState((prev) => ({
          ...prev,
          sourceTemplateId: template.template_id,
          orderType,
          isPrefilled: true,
          editSnapshot: null,
          editIndex: null,
          pendingNewJob: false,
          // Carry the template's QR check-in flag forward as the default; the
          // billing-account fetch will still gate visibility based on the
          // customer setting.
          verificationEnabled: !!template.verification_enabled,
          // The persisted template payload is the *submission* payload —
          // a single job draft was already fanned out into one entry per
          // shift/day. Collapse those clones back to one draft per
          // logical job so a single-skill repeat doesn't surface N blank
          // duplicate skill cards on the Submission step.
          jobs: dedupeTemplateJobs(
            template.jobs as Array<Record<string, unknown>>,
          ).map((job) => ({
            ...defaultJobDraftV2(orderType),
            ...(job as JobDraftV2),
            // Rates are intentionally NOT carried over from the template.
            // A repeat order should always price against the *current*
            // market rate, not the (possibly stale) rate stored on the
            // source order. Resetting these to 0 keeps `job0HasRates`
            // false so `useRateQuote` prefills pay/cost from the live
            // rate-quote endpoint once account + skill + address are set.
            pay_rate: 0,
            cost_rate: 0,
            original_pay_rate: 0,
            original_cost_rate: 0,
            shifts: [],
            job_type: orderType,
          })),
          step: 0,
        }));
      },
      clearTemplate: () =>
        setState((prev) => ({
          ...prev,
          sourceTemplateId: null,
          isPrefilled: false,
          editSnapshot: null,
          editIndex: null,
          pendingNewJob: false,
          jobs: prev.orderType
            ? [
                withCertificateDefaults(
                  defaultJobDraftV2(prev.orderType),
                  activeCertificateDefaults(prev),
                ),
              ]
            : [],
        })),
      setStep: (step: number) => setState((prev) => ({ ...prev, step })),
      setJobs: (jobs: JobDraftV2[]) => setState((prev) => ({ ...prev, jobs })),
      addNewJobDraft: () => setState((prev) => addNewJobDraftReducer(prev)),
      cancelNewJobDraft: () =>
        setState((prev) => cancelNewJobDraftReducer(prev)),
      editJobDraft: (index: number) =>
        setState((prev) => editJobDraftReducer(prev, index)),
      cancelEditJobDraft: () =>
        setState((prev) => cancelEditJobDraftReducer(prev)),
      finishNewJobDraft: () =>
        setState((prev) => finishNewJobDraftReducer(prev)),
      finishEditJobDraft: () =>
        setState((prev) => finishEditJobDraftReducer(prev)),
      setVerificationEnabled: (enabled: boolean) =>
        setState((prev) => ({ ...prev, verificationEnabled: enabled })),
      setVerificationVisible: (visible: boolean, defaultEnabled: boolean) =>
        setState((prev) => ({
          ...prev,
          verificationVisible: visible,
          // Only seed the default when the field becomes visible and is not
          // already prefilled from a template; otherwise keep the user's
          // current selection.
          verificationEnabled: prev.isPrefilled
            ? prev.verificationEnabled
            : visible
              ? defaultEnabled
              : false,
        })),
      // Re-checks the prefill / already-picked guards inside the
      // setState callback to avoid races with template apply or user
      // edits. Called by useCertificates after the customer's
      // pre-attached defaults come back. The reducer logic lives in
      // `./stateReducers` so it can be unit-tested independently of any
      // hook implementation.
      autoSeedCertificateDefaults: (
        defaultIds: number[],
        forBillingAccountId: number | null,
      ) =>
        setState((prev) =>
          autoSeedCertificateDefaultsReducer(
            prev,
            defaultIds,
            forBillingAccountId,
          ),
        ),
      // Re-checks the "no rates set yet" guard inside the setState
      // callback so a tenant admin's manual edits aren't clobbered by
      // a background refetch. Called by useRateQuote.
      applyRateQuote: (pay: number, cost: number) =>
        setState((prev) => applyRateQuoteReducer(prev, pay, cost)),
    }),
    [],
  );

  useVerificationFlag(customerId, actions.setVerificationVisible);
  const { certificateOptions } = useCertificates(
    billingAccountId,
    state.isPrefilled,
    actions.autoSeedCertificateDefaults,
    // Re-arms the seed when an earlier step overwrites a seeded draft with a
    // stale snapshot. Ignores jobs the admin has touched — see stateReducers.
    unseededDraftsSignature(state.jobs),
  );
  useRateQuote(
    billingAccountId,
    job0SkillId,
    job0AddressId,
    job0HasRates,
    actions.applyRateQuote,
  );

  const [showPostSubmitModal, hidePostSubmitModal] = useModal(
    () => (
      <PostSubmitModal
        orderId={orderData!.orderId}
        hireMessage={orderData!.hireMessage}
        staffingErrors={orderData!.staffingErrors}
        hideModal={() => {
          hidePostSubmitModal();
          actions.clearTemplate();
          actions.setStep(0);
          setViewMode(VIEW_ENTRY);
          setOrderData(null);
        }}
      />
    ),
    [orderData, actions],
  );

  useEffect(() => {
    if (orderData) showPostSubmitModal();
  }, [orderData, showPostSubmitModal]);

  const { fetchTemplate } = useOrderTemplate();
  const onSelectTemplate = async (templateId: number) => {
    const template = await fetchTemplate(templateId);
    const templateAccount =
      billingAccounts.find(
        (account) => account.id === template.billing_account_id,
      ) || null;
    actions.setBillingAccount(templateAccount);
    actions.applyTemplate(template);
    // Every distinct attempt (incl. repeat-from-template) emits its own
    // order_creation_started, which re-stamps the flow clock and resets the
    // pay-rate edit count. Fire BEFORE overriding the flow client below, since
    // trackOrderCreationStarted resets it to the admin default.
    trackOrderCreationStarted({
      order_type: template.order_type === 'LTA' ? 'LTA' : 'GIG',
    });
    // Part 4: upgrade the flow client to the chosen billing account's customer.
    setOrderFlowClientId(templateAccount?.customer_id ?? null);
    // Reset step timer + step-view dedup so this new session's first
    // step_viewed fires; stash the template id so the submit handler
    // can fire `order_reposted` with both ids once the new order lands.
    const now = getOrderFlowStartMs() ?? Date.now();
    setFlowStartMs(now);
    setStepStartMs(now);
    lastViewedKeyRef.current = null;
    setRepostFromTemplateId(templateId);
    setViewMode(VIEW_WIZARD);
  };

  const closeSnippetPanel = () => {
    setSnippetPanel(closedPanel);
    setSnippetError(null);
  };

  const openBrowseSaved = async (
    fieldKind: SavedSnippetFieldKind,
    title: string,
    onApply: SnippetApplyHandler,
  ) => {
    if (!state.billingAccount) return;
    setSnippetError(null);
    setSnippetPanel({ mode: 'browse', fieldKind, title, onApply });
    setBrowseLoading(true);
    try {
      const snippets = await createOrderV2Client.listSavedSnippets(
        state.billingAccount.id,
        fieldKind,
      );
      setBrowseSnippets(snippets);
    } catch (error) {
      setSnippetError(
        (error as Error).message || 'Failed to load saved templates',
      );
    } finally {
      setBrowseLoading(false);
    }
  };

  const openSaveSnippet = (
    fieldKind: SavedSnippetFieldKind,
    title: string,
    currentBody: string,
  ) => {
    setSnippetError(null);
    setSnippetPanel({ mode: 'save', fieldKind, title, currentBody });
  };

  const handleApplySnippet = async (snippet: SavedSnippet) => {
    if (snippetPanel.mode !== 'browse') return;
    setApplyingSnippetId(snippet.id);
    try {
      await createOrderV2Client.applySavedSnippet(snippet.id);
      snippetPanel.onApply(snippet.body);
      // Refresh local cache so the used_count badge reflects the new value
      // the next time the user opens the modal.
      if (state.billingAccount) {
        const refreshed = await createOrderV2Client.listSavedSnippets(
          state.billingAccount.id,
          snippetPanel.fieldKind,
        );
        setBrowseSnippets(refreshed);
      }
      closeSnippetPanel();
    } catch (error) {
      setSnippetError((error as Error).message || 'Failed to apply template');
    } finally {
      setApplyingSnippetId(null);
    }
  };

  const handleSaveSnippet = async ({
    name,
    body,
  }: {
    name: string;
    body: string;
  }) => {
    if (snippetPanel.mode !== 'save') return;
    if (!state.billingAccount) return;
    setSavingSnippet(true);
    setSnippetError(null);
    try {
      await createOrderV2Client.createSavedSnippet(state.billingAccount.id, {
        field_kind: snippetPanel.fieldKind,
        title: name,
        body,
      });
      // Vexo: snippet save is the closest "template saved" surface in this
      // wizard. `kind: 'snippet'` lets analysts split it from full order
      // templates if/when those get a save UI.
      trackTemplateSaved({ template_id: null, kind: 'snippet' });
      await refreshSnippetsForKind(snippetPanel.fieldKind);
      closeSnippetPanel();
    } catch (error) {
      setSnippetError((error as Error).message || 'Failed to save template');
    } finally {
      setSavingSnippet(false);
    }
  };

  const isMarkup = state.billingAccount?.rate_type === 'MARKUP';
  // Legacy v1 review heading reads "Order for ${billing.customer.name}" —
  // resolve the parent customer's name out of the directory so the v2 review
  // header can mirror that instead of showing the billing account label.
  const customerName =
    state.billingAccount?.customer_id != null
      ? (directoryCustomers.find(
          (entry) => entry.id === state.billingAccount?.customer_id,
        )?.name ?? null)
      : null;
  const stepLabels = isMarkup ? MARKUP_STEP_LABELS : BASE_STEP_LABELS;
  const stepCount = stepLabels.length;
  // Pay Rate is the second-to-last step on markup billing accounts, slotted
  // between Instructions (step 3) and the Review/Submission step.
  const payRateStepIndex = isMarkup ? 4 : -1;
  const reviewStepIndex = stepCount - 1;

  const renderStep = () => {
    const jobs =
      state.jobs.length > 0
        ? state.jobs
        : [defaultJobDraftV2(state.orderType || 'GIG')];
    if (state.step === payRateStepIndex) {
      return (
        <PayRateStep
          jobs={jobs}
          onUpdateJobs={actions.setJobs}
          skills={skills}
          billingAccount={state.billingAccount}
          onValidityChange={setPayRateValid}
        />
      );
    }
    switch (state.step) {
      case 0:
        return (
          <JobTypeStep
            jobs={jobs}
            onUpdateJobs={actions.setJobs}
            skills={skills}
            skillsLoading={skillsLoading}
            skillsSettled={skillsSettled}
            skillsError={skillsError}
            categories={skillCategories}
            onSkillSearchChange={setSkillSearch}
            locations={locations}
            onCreateLocation={async (payload) => {
              if (!state.billingAccount) return null;
              const created = await createOrderV2Client.createLocation(
                state.billingAccount.id,
                payload,
              );
              await refreshLocations();
              return created;
            }}
            supervisors={shiftSupervisors}
            onCreateSupervisor={async (payload) => {
              if (!state.billingAccount) return null;
              const created = await createOrderV2Client.createShiftSupervisor(
                state.billingAccount.id,
                payload,
              );
              await refreshShiftSupervisors();
              return created;
            }}
            onBrowseSaved={openBrowseSaved}
            onSaveSnippet={openSaveSnippet}
            addressSnippets={addressSnippets}
            checkInSnippets={checkInSnippets}
          />
        );
      case 1:
        return (
          <ScheduleStep
            jobs={jobs}
            onUpdateJobs={actions.setJobs}
            skills={skills}
            orderType={state.orderType}
            billingAccount={state.billingAccount}
            locations={locations}
            surgeRates={surgeRates}
            onRatesValidityChange={setScheduleRatesValid}
          />
        );
      case 2:
        return (
          <StaffingStep
            orderType={state.orderType}
            billingAccount={state.billingAccount}
            jobs={jobs}
            onUpdateJobs={actions.setJobs}
            skills={skills}
            substep={resolvedStaffingSubstep}
            certificateOptions={certificateOptions}
            onOvertimeOutcome={handleStaffingOvertimeOutcome}
            locations={locations}
          />
        );
      case 3:
        return (
          <InstructionsStep
            jobs={jobs}
            onUpdateJobs={actions.setJobs}
            uniforms={uniforms}
            uniformsLoading={uniformsLoading}
            uniformsError={uniformsError}
            onBrowseSaved={openBrowseSaved}
            onSaveSnippet={openSaveSnippet}
          />
        );
      default:
        return (
          <ReviewStep
            orderType={state.orderType}
            jobs={jobs}
            skills={skills}
            skillsLoading={skillsLoading}
            skillsSettled={skillsSettled}
            paymentMethods={paymentMethods}
            paymentMethodsLoading={paymentMethodsLoading}
            paymentMethodsError={paymentMethodsError}
            billingAccountName={state.billingAccount?.name || null}
            customerName={customerName}
            billingAccount={state.billingAccount}
            locations={locations}
            onUpdateJobs={actions.setJobs}
            onRatesValidityChange={setReviewRatesValid}
            onPaymentMethodValidityChange={setPaymentMethodValid}
            onSkillsValidityChange={setReviewSkillsValid}
            onShiftsValidityChange={setReviewShiftsValid}
            onAddSkill={() => {
              // Mirrors legacy CreateGigOrder: starting a new skill must
              // begin a fresh draft so the wizard doesn't overwrite the
              // already-completed job(s). The fresh draft lives at
              // jobs[0] (where every step reads/writes); previously
              // completed jobs shift to indices 1+.
              actions.addNewJobDraft();
              actions.setStep(0);
            }}
            onEditJob={(index) => {
              // Re-enter the wizard for an existing job. editJobDraft
              // move-to-fronts it into jobs[0] (where every step
              // reads/writes) so the wizard pre-populates from its
              // template values; step 0 restarts the flow. This is the
              // only path to give a multi-skill template's non-first
              // skills a schedule/rates.
              actions.editJobDraft(index);
              actions.setStep(0);
            }}
            surgeRates={surgeRates}
          />
        );
    }
  };

  /*
   * GRAVY-10881. A shift posted inside a surge tier window surges on the next
   * sweep (Product, 2026-10-06: keep that, but tell the admin first). graphy's
   * posting preview says which shifts of this order will, using the sweep's
   * own rules. Surge pay is a Tenant Admin surface, as the job banner is.
   *
   * The warning is informational, so it never blocks an order: if the
   * preview cannot be read the order is placed as before.
   */
  const surgeOnPosting = async (
    payload: ReturnType<typeof buildCreatePayload>,
  ): Promise<PostingSurgeItem[]> => {
    if (!currentAdminIsTenantAdminRole || customerId == null) return [];
    const inputs = postingPreviewInputs(payload);
    if (inputs.length === 0) return [];
    try {
      const preview = await paySurgeClient.previewPosting(customerId, inputs);
      return postingSurgeItems(preview, payload, skills);
    } catch {
      return [];
    }
  };

  const submitOrder = async () => {
    const payload = buildCreatePayload(state, locations, surgeRates);
    if (!payload.account_id) return;
    setSubmitLoading(true);
    const surging = await surgeOnPosting(payload);
    if (surging.length > 0) {
      setSubmitLoading(false);
      setPostingSurge({ payload, items: surging });
      return;
    }
    await placeOrder(payload);
  };

  const placeOrder = async (payload: ReturnType<typeof buildCreatePayload>) => {
    setSubmitLoading(true);
    setSubmitError(null);
    const startTime = Date.now();
    try {
      // Mirrors legacy CreateGigOrder: emit a funnel event when the user
      // submits an order that includes a certificate/badge selection.
      if (
        (payload.jobs?.[0] as { certificates?: number[] } | undefined)
          ?.certificates?.length
      ) {
        logEvent(GAEvent.BadgeAddedOnJobCreation);
      }

      const response = await createOrderV2Client.createOrder(payload);
      if (!response?.success) {
        const message = response?.errors?.[0] || 'Failed to place order';
        setSubmitError(message);
        return;
      }
      const orderId = response?.data?.order?.id;
      if (orderId == null) {
        setSubmitError('Order placed but response is missing order id');
        return;
      }
      const hiredCount = response?.data?.workers_hired || 0;
      // Vexo: the canonical "order submitted" event (CSV row #8). Counts
      // mirror the legacy GA funnel events but go to the Vexo dashboard.
      // `shifts_required` is total shifts across all jobs in the order.
      // `pay_rate` reads the first job's rate (cents → dollars); when the
      // order spans multiple jobs the rate is typically shared, so the
      // first job's rate is the representative value. `region` resolves
      // through the locations cache against the first job's address_id.
      // The order is already created at this point. Isolate analytics in its
      // own try/catch so a throw in the value adapters (which run before
      // track()'s internal guard) can't trip the submit catch below and show a
      // false "order failed" toast — which would invite a duplicate re-submit.
      try {
        const totalShifts = (payload.jobs ?? []).reduce(
          (acc: number, j: any) => acc + (j?.shifts?.length ?? 0),
          0,
        );
        const firstJobRateCents = state.jobs[0]?.pay_rate ?? 0;
        const payRateDollars =
          firstJobRateCents > 0 ? firstJobRateCents / 100 : null;
        const submitAddressId = state.jobs[0]?.address_id ?? null;
        const submitRegion =
          submitAddressId != null
            ? (locations.find((l) => l.id === submitAddressId)?.state ?? null)
            : null;
        trackOrderSubmitted({
          order_id: orderId,
          shifts_required: totalShifts,
          pay_rate: payRateDollars,
          shift_type:
            state.jobs[0]?.staffing_strategy === 'SEND_INVITATIONS'
              ? 'invited'
              : 'public',
          region: submitRegion,
          time_to_create_mins: Math.max(
            0,
            Math.round((Date.now() - flowStartMs) / 60_000),
          ),
          total_value: computeOrderTotalValue(
            buildOrderValueJobsFromState(state),
          ),
        });
        // Vexo: if this wizard run was launched from a template, the new
        // order is the "repost" of that template's source order. Fire once
        // both ids are known. `time_since_original_days` isn't in scope
        // here — the template payload doesn't carry its creation date —
        // so send null until the API surfaces it.
        if (repostFromTemplateId != null) {
          trackOrderReposted({
            original_order_id: repostFromTemplateId,
            new_order_id: orderId,
            time_since_original_days: null, // TODO plumb when template type exposes created_at
          });
          setRepostFromTemplateId(null);
        }
      } catch {
        /* analytics must never fail a successful order */
      }
      // Mirrors legacy: log the final-step submission funnel event tagged by
      // billing rate type (markup vs static) and order type (gig vs lta).
      const isMarkupAccount = state.billingAccount?.rate_type === 'MARKUP';
      const isLtaOrder = state.orderType === 'LTA';
      const stepEvent = isMarkupAccount
        ? isLtaOrder
          ? GAEvent.StepSixMarkupLta
          : GAEvent.StepSixMarkupGig
        : isLtaOrder
          ? GAEvent.StepFiveStaticLta
          : GAEvent.StepFiveStaticGig;
      const stepEventValue = isMarkupAccount
        ? state.billingAccount?.id
        : (orderId as number | string);
      logEvent(stepEvent, stepEventValue, {
        time_spent: (Date.now() - startTime) / 1000,
        platform: isWeb() ? 'web' : isIOS() ? 'ios' : 'android',
      });

      // The backend upserts the billing-account order template during
      // order creation, so the cached Repeat-orders list is now stale.
      // Invalidate every order_templates slot (all billing accounts /
      // order types) so the always-mounted useOrderTemplatesList query
      // refetches before the user lands back on the entry screen.
      queryClient.invalidateQueries({
        queryKey: [...orderV2QueryKeys.root, 'order_templates'],
      });

      // GRAVY-10310: Dashboard V1's weekly grid is an Apollo cache-first query,
      // so it does not learn about an order created over REST. Without this,
      // returning to the dashboard by any path that isn't the sidebar/navbar
      // Dashboard link — browser Back especially — replays the cached week and
      // the new order is invisible until a reload or the 120s poll.
      evictDashboardShiftWeeks(apolloClient.cache, currentAgency?.id);

      setOrderData({
        orderId,
        hireMessage:
          hiredCount > 0
            ? `${hiredCount} worker${hiredCount === 1 ? '' : 's'} hired.`
            : null,
        // GRAVY-10803. A 201 can still carry `partial_success` + `errors` when
        // some of the hires requested inside the order were refused — most
        // visibly a worker missing a badge the job requires. `errors` on a
        // SUCCESS response means that; the catch block below reads the same
        // key for an outright failure.
        staffingErrors: response?.errors ?? [],
      });
    } catch (error: any) {
      const message =
        error?.response?.data?.errors?.[0] ||
        error?.response?.data?.message ||
        error?.message ||
        'Failed to place order';
      setSubmitError(message);
    } finally {
      setSubmitLoading(false);
    }
  };

  const currentStrategy = state.jobs[0]?.staffing_strategy ?? null;
  const isStaffingStep = state.step === 2;
  const isLta = state.orderType === 'LTA';
  // GIG users must pick a strategy before advancing past the choice cards.
  // LTA implicitly uses Send Invitations, so it never blocks here.
  const showContinueToInvite =
    isStaffingStep &&
    !isLta &&
    staffingSubstep === 'choice' &&
    currentStrategy === 'SEND_INVITATIONS';

  const canAdvance = () => {
    if (state.step === 0) {
      const job = state.jobs[0];
      if (
        !job ||
        job.skill_id == null ||
        job.address_id == null ||
        job.contact_id == null
      ) {
        return false;
      }
      // Templates persist skill_id directly, but the V2 skills endpoint
      // only returns active + requestable skills for the account's
      // agency. If the template's skill is no longer in that list, block
      // advance so the user can pick a still-available skill instead of
      // proceeding with a blank display. Key off `skillsSettled` (true
      // even for a resolved *empty* list) rather than `skills.length > 0`
      // — an empty account skills response must still block, not silently
      // pass a blank repeat skill. `skillsSettled` stays false during the
      // post-template-apply fan-out so this never false-blocks in flight.
      if (skillsSettled && !skills.some((skill) => skill.id === job.skill_id)) {
        return false;
      }
      return true;
    }
    if (state.step === 1) {
      // Must have at least one shift AND, for STATIC tenant-admin
      // editable rates, the typed pay/bill must satisfy the minimum.
      // scheduleRatesValid defaults to true so client admins / markup
      // accounts / pre-fetched flows are never blocked here.
      return (state.jobs[0]?.shifts?.length || 0) > 0 && scheduleRatesValid;
    }
    if (state.step === 3) return !!state.jobs[0]?.uniform_id;
    if (isStaffingStep && !isLta && staffingSubstep === 'choice') {
      return currentStrategy !== null;
    }
    if (state.step === payRateStepIndex) return payRateValid;
    return true;
  };

  const handleBack = () => {
    if (isStaffingStep && staffingSubstep === 'invite' && !isLta) {
      setStaffingSubstep('choice');
      return;
    }
    // Reset the step timer so `time_on_step_sec` on the next forward
    // step doesn't include time spent on earlier steps after a backtrack.
    setStepStartMs(Date.now());
    actions.setStep(Math.max(0, state.step - 1));
  };

  const handleContinue = () => {
    if (showContinueToInvite) {
      // Vexo: fire the invites sub-step view (_3b) on the explicit button
      // press — not as a side effect of the invite screen mounting. Stamp
      // the dedup ref to the invites key BEFORE flipping the sub-step so the
      // view-effect (which also watches the sub-step change) treats the key
      // as already-emitted and doesn't double-fire. LTA has no choice→invite
      // button, so it keeps firing _3b from the effect on staffing entry —
      // only this GIG button transition moves to the click.
      const inviteKey = stepKeyFor(state.step, isMarkup, 'invite');
      if (inviteKey && lastViewedKeyRef.current !== inviteKey) {
        lastViewedKeyRef.current = inviteKey;
        trackOrderCreationStepViewed({
          step: inviteKey,
          time_since_flow_start_sec: Math.max(
            0,
            Math.round((Date.now() - flowStartMs) / 1000),
          ),
        });
      }
      setStaffingSubstep('invite');
      return;
    }
    // Vexo: tag the step the user just completed (not the one they're
    // advancing to) so funnel reports show "completed Schedule" not
    // "now on Staffing".
    const completedIndex = state.step;
    const completedKey = stepKeyFor(
      completedIndex,
      isMarkup,
      resolvedStaffingSubstep,
    );
    if (completedKey) {
      const staffingAggregates =
        completedIndex === 2
          ? buildStaffingAggregates(state.jobs, staffingOTCounters.current)
          : undefined;
      trackOrderCreationStepCompleted({
        step: completedKey,
        time_on_step_sec: Math.max(
          0,
          Math.round((Date.now() - stepStartMs) / 1000),
        ),
        staffing_aggregates: staffingAggregates,
      });
    }
    setStepStartMs(Date.now());

    const nextStep = Math.min(stepCount - 1, state.step + 1);
    // V1 parity for multi-skill orders: when the wizard's add-skill
    // flow finishes (Continue advances into the Review step) the
    // freshly added `jobs[0]` rotates to the end of `jobs`, so the
    // first-added skill stays on top of the Review list and the
    // submission/template payload serializes in V1's append order.
    // The reducer no-ops when there's no pending new job, so initial
    // walk-throughs and repeat-order traversals are unaffected.
    if (nextStep === reviewStepIndex && state.pendingNewJob) {
      actions.finishNewJobDraft();
    }
    // Finalize an in-place edit: when the user re-enters Review after
    // editing an existing skill, splice the modified `jobs[0]` back to
    // the original index it was picked from. Without this rotation,
    // each Review-step edit on a repeat order would leave the edited
    // job stranded at index 0, and the next repeat order's template
    // would drift toward last-edited-first ordering. The reducer
    // no-ops when no edit is in flight.
    if (nextStep === reviewStepIndex && state.editSnapshot) {
      actions.finishEditJobDraft();
    }
    actions.setStep(nextStep);
  };

  return (
    <OrderStateContext.Provider value={state}>
      <OrderActionsContext.Provider value={actions}>
        {viewMode === VIEW_ENTRY ? (
          <>
            <OrderV2EntryOnboarding orderType={state.orderType} />
            <EntryScreen
              billingAccounts={billingAccounts}
              directoryCustomers={directoryCustomers}
              onRefreshDirectory={refreshDirectory}
              templates={templates}
              selectedBillingAccountId={state.billingAccount?.id || null}
              orderType={state.orderType}
              paymentMethods={paymentMethods}
              paymentMethodsLoading={paymentMethodsLoading}
              paymentMethodsError={paymentMethodsError}
              onSelectBillingAccount={(account) => {
                actions.setBillingAccount(account);
              }}
              onPickTemplate={onSelectTemplate}
              onStartOrder={() => {
                actions.clearTemplate();
                actions.setStep(0);
                const orderType = state.orderType || 'GIG';
                if (!state.orderType) {
                  actions.setOrderType(orderType);
                }
                actions.setJobs([defaultJobDraftV2(orderType)]);
                // Every distinct attempt emits its own order_creation_started,
                // which re-stamps the flow clock and resets the pay-rate edit
                // count. Fire BEFORE overriding the flow client below, since
                // trackOrderCreationStarted resets it to the admin default.
                trackOrderCreationStarted({ order_type: orderType });
                // Part 4: upgrade the flow client to the chosen billing account.
                setOrderFlowClientId(state.billingAccount?.customer_id ?? null);
                // Fresh wizard entry (not from template): reset step timer
                // and step-view dedup, and clear any prior template-id stash.
                const now = getOrderFlowStartMs() ?? Date.now();
                setFlowStartMs(now);
                setStepStartMs(now);
                lastViewedKeyRef.current = null;
                setRepostFromTemplateId(null);
                setViewMode(VIEW_WIZARD);
              }}
            />
          </>
        ) : null}

        {viewMode === VIEW_WIZARD ? (
          <WizardShell>
            <WizardHeader
              title={
                state.editSnapshot
                  ? `Editing ${state.orderType === 'LTA' ? 'long-term' : 'short-term'} shift`
                  : `New ${state.orderType === 'LTA' ? 'long-term' : 'short-term'} shift`
              }
              steps={stepLabels}
              currentStep={state.step}
              onHelp={() =>
                startTour(stepKeyFor(state.step, isMarkup, staffingSubstep))
              }
              onClose={() => {
                // If the user has made meaningful progress, warn before
                // discarding the draft. Otherwise close immediately,
                // matching v1's `state.completedStep` gate on
                // `ExitJobEditorModal`.
                if (state.step > 0 || state.isPrefilled) {
                  setExitConfirmOpen(true);
                  return;
                }
                actions.clearTemplate();
                actions.setStep(0);
                setViewMode(VIEW_ENTRY);
              }}
            />
            <WizardBody>{renderStep()}</WizardBody>
            {submitError ? <ErrorBlock>{submitError}</ErrorBlock> : null}
            <StickyFooter>
              {state.step > 0 ? (
                <FooterGhost
                  type="button"
                  disabled={submitLoading}
                  onClick={handleBack}
                >
                  {isStaffingStep && staffingSubstep === 'invite' && !isLta
                    ? 'Back'
                    : 'Go back'}
                </FooterGhost>
              ) : state.editSnapshot ? (
                // Step 0 while editing an existing job in place: restore
                // the pre-edit job list and return to review. Discarding
                // in-progress edits mirrors how the add-skill path's "Go
                // back" unwinds an unwanted new draft.
                <FooterGhost
                  type="button"
                  disabled={submitLoading}
                  onClick={() => {
                    actions.cancelEditJobDraft();
                    actions.setStep(reviewStepIndex);
                  }}
                >
                  Go back
                </FooterGhost>
              ) : state.jobs.length > 1 ? (
                // Step 0 with an in-progress additional skill: bail out
                // of the new draft and return to review, instead of
                // wiping the whole order back to the entry screen.
                <FooterGhost
                  type="button"
                  disabled={submitLoading}
                  onClick={() => {
                    actions.cancelNewJobDraft();
                    actions.setStep(reviewStepIndex);
                  }}
                >
                  Go back
                </FooterGhost>
              ) : (
                <FooterCancel
                  type="button"
                  onClick={() => {
                    actions.clearTemplate();
                    actions.setStep(0);
                    setViewMode(VIEW_ENTRY);
                  }}
                >
                  Cancel
                </FooterCancel>
              )}
              {state.step === reviewStepIndex ? (
                <FooterPrimary
                  type="button"
                  data-tour="orderflow-next"
                  disabled={
                    submitLoading ||
                    !reviewRatesValid ||
                    !paymentMethodValid ||
                    !reviewSkillsValid ||
                    !reviewShiftsValid
                  }
                  onClick={submitOrder}
                >
                  {submitLoading ? 'Placing order...' : 'Place new order'}
                </FooterPrimary>
              ) : (
                <FooterPrimary
                  type="button"
                  data-tour="orderflow-next"
                  disabled={!canAdvance() || submitLoading}
                  onClick={handleContinue}
                >
                  {showContinueToInvite ? 'Continue to Invite' : 'Continue'}
                </FooterPrimary>
              )}
            </StickyFooter>

            <BrowseSavedModal
              isOpen={snippetPanel.mode === 'browse'}
              title={snippetPanel.mode === 'browse' ? snippetPanel.title : ''}
              snippets={browseSnippets}
              loading={browseLoading}
              applying={applyingSnippetId !== null}
              onCancel={closeSnippetPanel}
              onApply={handleApplySnippet}
            />

            <SaveSnippetModal
              isOpen={snippetPanel.mode === 'save'}
              title={snippetPanel.mode === 'save' ? snippetPanel.title : ''}
              initialBody={
                snippetPanel.mode === 'save' ? snippetPanel.currentBody : ''
              }
              saving={savingSnippet}
              errorMessage={snippetError}
              onCancel={closeSnippetPanel}
              onSave={handleSaveSnippet}
            />

            {snippetError && snippetPanel.mode === 'closed' ? (
              <ErrorBlock>{snippetError}</ErrorBlock>
            ) : null}

            {postingSurge && (
              <PostingSurgeDialog
                items={postingSurge.items}
                confirmLabel="Place order"
                busy={submitLoading}
                onCancel={() => setPostingSurge(null)}
                onConfirm={() => {
                  const { payload } = postingSurge;
                  setPostingSurge(null);
                  placeOrder(payload);
                }}
              />
            )}

            <ExitWizardModal
              isOpen={exitConfirmOpen}
              onCancel={() => setExitConfirmOpen(false)}
              onConfirm={() => {
                setExitConfirmOpen(false);
                actions.clearTemplate();
                actions.setStep(0);
                setViewMode(VIEW_ENTRY);
              }}
            />
          </WizardShell>
        ) : (
          <>
            <BrowseSavedModal
              isOpen={snippetPanel.mode === 'browse'}
              title={snippetPanel.mode === 'browse' ? snippetPanel.title : ''}
              snippets={browseSnippets}
              loading={browseLoading}
              applying={applyingSnippetId !== null}
              onCancel={closeSnippetPanel}
              onApply={handleApplySnippet}
            />
            <SaveSnippetModal
              isOpen={snippetPanel.mode === 'save'}
              title={snippetPanel.mode === 'save' ? snippetPanel.title : ''}
              initialBody={
                snippetPanel.mode === 'save' ? snippetPanel.currentBody : ''
              }
              saving={savingSnippet}
              errorMessage={snippetError}
              onCancel={closeSnippetPanel}
              onSave={handleSaveSnippet}
            />
          </>
        )}
      </OrderActionsContext.Provider>
    </OrderStateContext.Provider>
  );
};

// Route-level guard: tenant and customer admins pass (see
// useOrderFlowV2Enabled). This stops any other admin kind, e.g. helpdesk,
// reaching the wizard by direct URL.
const AgencyCreateOrderV2Guard = () => {
  const orderFlowV2Enabled = useOrderFlowV2Enabled();
  if (!orderFlowV2Enabled) {
    return <Navigate to="/restricted-access" replace />;
  }
  return <AgencyCreateOrderV2 />;
};

export default AgencyCreateOrderV2Guard;
